// glonet-react-tailwind-project--src--components--AffiliateDashboard.js
// The affiliate's home at /affiliates/dashboard (owner directive 2026-08-12),
// evolved from the 2026-07-26 stats page (/affiliates/stats now lands here).
// Four tabs modeled on the admin Go To Market tab, minus everything an
// affiliate doesn't need:
//   Today    - your link, the one next step, your headline numbers, discount
//   Outreach - log what you sent, your own way; no send machinery on purpose
//   Messages - the words to send, link already inside, copy and go
//   Results  - signups, money, payouts; demotions are only fair if visible
// Honesty rule carried from the GTM Results screen: conversions are counted
// from the ?ref= link, never from the self-reported outreach log.
// ?preview=1 renders clearly-labeled SAMPLE numbers with no real data.
// ?as_user_id=N is the ADMIN view-as (2026-08-24): the real screen this
// affiliate sees, read from the same endpoints with the id appended. The admin
// Affiliates table answers "how is this person doing"; this answers "what are
// they looking at" — the thing you need when someone says their page is wrong.
// Looking, never acting: every write control is locked, and the backend
// refuses as_user_id on the write endpoints regardless of what the UI sends.
import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Auth } from "aws-amplify";
import { GLONET_AI_EVENT_ID } from "../config/glonetai";

const ACCENT = "#74a0f5";
const GREEN = "#57d9a4";
const AMBER = "#e0a35e";
const MONO = "'JetBrains Mono',monospace";
const SERIF = "'Instrument Serif',serif";

// Messages folded INTO Outreach 2026-08-26. The old Messages tab ended by
// telling you to "log it under Outreach" — and any time the UI has to send you
// to another tab, the seam is in the wrong place. Copying a message IS the
// moment you act, and one tab away is where logging dies. ?tab=messages still
// resolves (emailed links), it just lands on Outreach.
const TABS = [["today", "Today"], ["outreach", "Outreach"],
  ["results", "Results"], ["rooms", "Private Rooms"]];
const CHANNELS = [["linkedin", "LinkedIn"], ["x", "X"], ["email", "Email"], ["other", "Other"]];

// What we tell the affiliate after they log a post link. Every "no" here is a
// rule they can understand and work with, so each one says which rule it was.
const SHOUTOUT_NOTE = (so, hadUrl) => {
  if (!hadUrl) return "Logged.";
  if (!so) return "Logged.";
  if (so.scheduled && so.late) {
    return "Logged. That post is a few hours old, so we'll ask people over the next day "
      + "rather than right now — a fresh comment still puts it back in front of people.";
  }
  if (so.scheduled) return "Logged — we're asking a few affiliates to take a look.";
  const why = {
    "weekly-post-cap": `Logged. That's your ${so.cap || 2} posts for the week, so this one goes out `
      + "without a shout-out — it keeps everyone's inbox worth opening.",
    "already-registered": so.mine
      ? "Logged. You'd already shared that one with the crew."
      : "Logged. Someone already put that post in front of the crew.",
    "bad-url": "Logged, but that link didn't look like a post URL — paste the link to the post itself.",
    "unsupported-host": "Logged. Shout-outs work on LinkedIn and X posts for now.",
  }[so.reason];
  return why || "Logged.";
};

const PREVIEW_STATS = {
  has_link: true, affiliate_code: "SAMPLE", tier: "partner", pct: 50,
  window_months: 12, referral_discount_pct: 60, suspended: false, payout_ready: true,
  member_since: "2025-11-01T00:00:00Z",
  signups: 486, profiled: 213, signups_90d: 121, profiled_90d: 104,
  partner_keep_target: 100, partner_earn_target: 100, partner_quota: 100, partners_used: 37,
  outreach_entries: 3,
  owed_cents: 61230, paid_cents: 866240,
  paid_7d: 18250, paid_30d: 77000, paid_60d: 158500, paid_90d: 241000,
  share_link: "https://glonet.ai/?ref=SAMPLE",
  share_templates: [
    { template_id: 1, name: "Share · Email to your list", channel: "email",
      subject: "Something I think is worth your time",
      body: "I have been trying out Glonet.ai and I think some of you should be on it.\n\n"
        + "It matches you with people worth meeting — not a feed, not a job board — and\n"
        + "there is a live venue where you actually talk to them.\n\n"
        + "It is free to join right now while the network fills up.\n\n"
        + "Here is my link: https://glonet.ai/?ref=SAMPLE",
      notes: "The workhorse. Longest of the five; a warm list will read it." },
    { template_id: 2, name: "Share · LinkedIn post", channel: "linkedin", subject: null,
      body: "Most networking is a room full of people hoping someone else starts talking.\n\n"
        + "Glonet.ai does the introduction for you.\n\n"
        + "Free to join while the network fills: https://glonet.ai/?ref=SAMPLE",
      notes: "Keep it under ~120 words; LinkedIn truncates." },
    { template_id: 3, name: "Share · Direct message", channel: "other", subject: null,
      body: "Hey — this made me think of you. Glonet.ai matches you with people worth "
        + "meeting instead of making you dig through a feed. Free while it fills up: "
        + "https://glonet.ai/?ref=SAMPLE",
      notes: "For the ten people who matter most. Personal beats broadcast." },
  ],
  recent_payouts: [
    { at: "2026-07-25T00:00:00Z", stream: "subscription", amount_cents: 7150, status: "pending" },
    { at: "2026-07-24T00:00:00Z", stream: "subscription", amount_cents: 9250, status: "sent" },
    { at: "2026-07-21T00:00:00Z", stream: "subscription", amount_cents: 11000, status: "sent" },
    { at: "2026-07-19T00:00:00Z", stream: "one_time", amount_cents: 4500, status: "sent" },
    { at: "2026-07-15T00:00:00Z", stream: "subscription", amount_cents: 8750, status: "sent" },
    { at: "2026-07-11T00:00:00Z", stream: "subscription", amount_cents: 12000, status: "sent" },
  ],
  program: { partner_pct: 50, partner_window_months: 12, partner_slots: 100,
             standard_pct: 25, standard_window_months: 6 },
};
const PREVIEW_ROOMS = {
  enabled: true, is_affiliate: true, payout_ready: true,
  tickets: [{
    ticket_id: 1, title: "VC Office Hours", description: "An hour with two seed-stage VCs.",
    price_cents: 1000, room_code: "SAMPLE", free_code: "SAMPLEFR", room_token: "SAMPLETOKEN00000",
    active: true, buy_url: "https://glonet.ai/room-ticket/SAMPLE",
    sold: 14, free_redemptions: 2, delivered: 12, refunded: 0,
    revenue_cents: 14000, held_cents: 1748, releasing_cents: 0, paid_cents: 10488,
  }],
};
const PREVIEW_OUTREACH = {
  has_link: true, total_contacted: 74, outreach: [
    { outreach_id: 3, day: "2026-08-10", channel: "linkedin", contacted: 40, note: "the arcade floor last night",
      at: "2026-08-10T18:00:00Z",
      post: { post_id: 1, status: "reviving", asked: 6, looks: 4, shoutouts: 3, signups: 2,
        url: "https://www.linkedin.com/posts/sample", topic: "the arcade floor last night" } },
    { outreach_id: 2, day: "2026-08-08", channel: "email", contacted: 30, note: "Newsletter PS line", at: "2026-08-08T15:00:00Z" },
    { outreach_id: 1, day: "2026-08-07", channel: "linkedin", contacted: 4, note: "why I stopped doing intro calls",
      at: "2026-08-07T02:00:00Z",
      post: { post_id: 2, status: "stopped", asked: 5, looks: 2, shoutouts: 1, signups: 0,
        url: "https://www.linkedin.com/posts/sample2", topic: "why I stopped doing intro calls" } },
  ],
};

export default function AffiliateDashboard() {
  const navigate = useNavigate();
  const qs = typeof window !== "undefined" ? new URLSearchParams(window.location.search) : new URLSearchParams();
  const preview = qs.get("preview") === "1";
  const asUserId = qs.get("as_user_id") || "";
  const asParam = asUserId ? `&as_user_id=${encodeURIComponent(asUserId)}` : "";
  // Both modes look but never touch, so they lock the same controls.
  const locked = preview || !!asUserId;
  const initialTab = TABS.some(([k]) => k === qs.get("tab"))
    ? qs.get("tab")
    : (qs.get("tab") === "messages" ? "outreach" : "today");
  const [tab, setTabState] = useState(initialTab);
  const setTab = (k) => {
    setTabState(k);
    try {
      const u = new URL(window.location.href);
      u.searchParams.set("tab", k);
      window.history.replaceState(null, "", u.toString());
    } catch (e) { /* history is cosmetic */ }
  };
  const [state, setState] = useState(preview ? "ready" : "loading"); // loading | anon | ready | none
  const [s, setS] = useState(preview ? PREVIEW_STATS : null);
  const [copied, setCopied] = useState(false);
  const [copiedId, setCopiedId] = useState(null);

  useEffect(() => {
    if (preview) return;
    Auth.currentAuthenticatedUser()
      .then(() => import("../services/api"))
      .then(({ default: api }) => api.get(`affiliates/my-stats?event_id=${GLONET_AI_EVENT_ID}${asParam}`))
      .then(({ data }) => { setS(data); setState(data.has_link ? "ready" : "none"); })
      .catch((e) => setState(e?.response?.status === 403 ? "forbidden" : "anon"));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // ——— outreach log (lazy: fetched when the tab is first opened) ———
  const [outreach, setOutreach] = useState(preview ? PREVIEW_OUTREACH : null);
  const [logForm, setLogForm] = useState({ channel: "linkedin", contacted: "", note: "", day: "", post_url: "" });
  const [logBusy, setLogBusy] = useState(false);
  const [logNote, setLogNote] = useState(null);
  useEffect(() => {
    if (preview || tab !== "outreach" || outreach || state !== "ready") return;
    import("../services/api")
      .then(({ default: api }) => api.get(`affiliates/my-outreach?event_id=${GLONET_AI_EVENT_ID}${asParam}`))
      .then(({ data }) => setOutreach(data))
      .catch(() => setOutreach({ has_link: true, outreach: [], total_contacted: 0, error: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, state]);
  const submitLog = async () => {
    if (locked || logBusy) return;
    const n = Number(logForm.contacted);
    if (!Number.isFinite(n) || n < 0) { setLogNote("How many people? A number is needed."); return; }
    setLogBusy(true); setLogNote(null);
    try {
      const { default: api } = await import("../services/api");
      const { data } = await api.post("affiliates/my-outreach", {
        event_id: GLONET_AI_EVENT_ID, channel: logForm.channel, contacted: n,
        note: logForm.note.trim() || undefined, day: logForm.day || undefined,
        post_url: logForm.post_url.trim() || undefined,
      });
      setOutreach((o) => ({
        has_link: true,
        outreach: [data, ...((o && o.outreach) || [])],
        total_contacted: ((o && o.total_contacted) || 0) + (data.contacted || 0),
      }));
      setS((prev) => (prev ? { ...prev, outreach_entries: (prev.outreach_entries || 0) + 1 } : prev));
      setLogForm({ channel: logForm.channel, contacted: "", note: "", day: "", post_url: "" });
      // Say what happened to the link, in the affiliate's words. "Nothing was
      // scheduled" is a normal outcome (weekly cap, already registered, feature
      // off) and has to read as an explanation, not a failure.
      setLogNote(SHOUTOUT_NOTE(data.shoutout, logForm.post_url.trim()));
    } catch (e) {
      setLogNote("Could not save. Try again.");
    } finally { setLogBusy(false); }
  };

  // ——— private room tickets (owner rulings 2026-08-24; lazy like outreach) ———
  const [roomsData, setRoomsData] = useState(preview ? PREVIEW_ROOMS : null);
  const [rtForm, setRtForm] = useState({ title: "", price: "", description: "" });
  const [rtBusy, setRtBusy] = useState(false);
  const [rtNote, setRtNote] = useState(null);
  const [rtCopied, setRtCopied] = useState(null); // `${ticket_id}:${field}`
  useEffect(() => {
    if (preview || tab !== "rooms" || roomsData || state !== "ready") return;
    import("../services/api")
      .then(({ default: api }) => api.get(`subscriptions/room-tickets/mine${asUserId ? `?as_user_id=${encodeURIComponent(asUserId)}` : ""}`))
      .then(({ data }) => setRoomsData(data))
      .catch(() => setRoomsData({ enabled: false, is_affiliate: true, tickets: [], error: true }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, state]);
  const createTicket = async () => {
    if (locked || rtBusy) return;
    const cents = Math.round(Number(rtForm.price) * 100);
    if (!rtForm.title.trim()) { setRtNote("Give the room a title."); return; }
    if (!Number.isFinite(cents) || cents < 300) { setRtNote("Minimum ticket price is $3."); return; }
    setRtBusy(true); setRtNote(null);
    try {
      const { default: api } = await import("../services/api");
      await api.post("subscriptions/room-tickets", {
        title: rtForm.title.trim(), price_cents: cents,
        description: rtForm.description.trim() || undefined,
      });
      const { data } = await api.get("subscriptions/room-tickets/mine");
      setRoomsData(data);
      setRtForm({ title: "", price: "", description: "" });
      setRtNote("Ticket created — three codes below.");
    } catch (e) {
      setRtNote(e?.response?.data?.detail || "Could not create the ticket.");
    } finally { setRtBusy(false); }
  };
  const rtCopy = async (tid, field, text) => {
    try { await navigator.clipboard.writeText(text); } catch (e) { window.prompt("Copy:", text); return; }
    setRtCopied(`${tid}:${field}`);
    setTimeout(() => setRtCopied((c) => (c === `${tid}:${field}` ? null : c)), 1600);
  };

  // ——— member discount picker (owner ruling 2026-08-11) ———
  const [savingDiscount, setSavingDiscount] = useState(false);
  const [discountNote, setDiscountNote] = useState(null);
  const setDiscount = async (pctVal) => {
    if (locked || savingDiscount || (s?.referral_discount_pct ?? 60) === pctVal) return;
    setSavingDiscount(true); setDiscountNote(null);
    try {
      const { default: api } = await import("../services/api");
      const { data } = await api.post("affiliates/my-discount",
        { event_id: GLONET_AI_EVENT_ID, discount_pct: pctVal });
      setS((prev) => ({ ...prev, referral_discount_pct: data.referral_discount_pct }));
      setDiscountNote(data.referrals_raised > 0
        ? `Saved. ${data.referrals_raised} earlier signup${data.referrals_raised === 1 ? "" : "s"} got the deeper discount too.`
        : "Saved.");
    } catch (e) { setDiscountNote("Could not save. Try again."); }
    finally { setSavingDiscount(false); }
  };

  const shareLink = s?.share_link || (s?.affiliate_code ? `https://glonet.ai/?ref=${s.affiliate_code}` : "");
  const copyTemplate = async (t) => {
    const text = (t.subject ? `Subject: ${t.subject}\n\n` : "") + (t.body || "");
    try { await navigator.clipboard.writeText(text); }
    catch (e) { window.prompt("Copy this message:", text); return; }
    setCopiedId(t.template_id);
    setTimeout(() => setCopiedId((c) => (c === t.template_id ? null : c)), 1800);
    // Copying is the moment they act, so the log row below is filled in for
    // them right now: channel from the message, today's date, and a note that
    // says which one it was. All that's left is the number and the post link.
    if (locked) return;
    setLogForm((f) => ({
      ...f,
      channel: CHANNELS.some(([v]) => v === t.channel) ? t.channel : f.channel,
      note: f.note || `Sent: ${(t.name || "").replace(/^Share · /, "")}`,
    }));
  };
  const copy = async () => {
    try { await navigator.clipboard.writeText(shareLink); setCopied(true); setTimeout(() => setCopied(false), 1600); }
    catch (e) { window.prompt("Copy your link:", shareLink); }
  };

  const dollars = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
  const isPartner = s?.tier === "partner";
  // ONE performance rule (2026-08-12): the quota (2% of claimed audience,
  // 100-600) keeps Partner AND earns it for a Standard, rolling 90 days.
  const keepTarget = s?.partner_keep_target || 100;
  const earnTarget = s?.partner_earn_target || keepTarget;
  const progress = Math.min(1, (s?.profiled_90d || 0) / (isPartner ? keepTarget : earnTarget));

  const card = { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)",
    borderRadius: "18px", padding: "26px 28px" };
  const label = { fontFamily: MONO, fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", color: "#6b6f78" };
  const primaryBtn = { fontSize: 14, fontWeight: 600, color: "#0c0d10", background: ACCENT, border: "none",
    cursor: "pointer", padding: "11px 20px", borderRadius: 999 };
  const inputStyle = { background: "rgba(255,255,255,0.05)", color: "#f1f2f4",
    border: "1px solid rgba(255,255,255,0.16)", borderRadius: 10, padding: "10px 12px",
    fontSize: 14, fontFamily: "inherit" };

  // the one next step (Today): payouts, then a first outreach, then the quota
  const nextStep = !s ? null
    : !s.payout_ready
      ? { title: "Finish payout setup", why: "Anything you earn is held until Stripe setup is done.",
          cta: "Set up payouts",
          // Locked while looking: this walks into THEIR Stripe onboarding, which
          // an admin must never start on their behalf (nor a preview open at
          // all). The card still reads — knowing payouts are unfinished is
          // exactly what the admin came to find out.
          act: locked ? null : () => navigate(`/affiliate-onboarding/${s.affiliate_code}`) }
      : (s.outreach_entries || 0) === 0
        ? { title: "Send your first messages", why: "The words are written and your link is inside them. Send a few, then log what you sent.",
            cta: "Open your messages", act: () => setTab("messages") }
        : (s.profiled_90d || 0) < (isPartner ? keepTarget : earnTarget)
          ? { title: "Keep sharing", why: `Profiled signups are what count: ${s.profiled_90d || 0} of your ${isPartner ? keepTarget : earnTarget} quota this quarter${isPartner ? "" : ", which takes a Partner slot"}. A Water Cooler meetup with 20+ live attendees credits 20.`,
              cta: "Log outreach", act: () => setTab("outreach") }
          : { title: "Quota met", why: "Nothing needs you today. Check your results or send another wave when it suits you.",
              cta: "See results", act: () => setTab("results") };

  const FourNumbers = () => (
    <section className="ad-grid" style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 18, marginTop: 18 }}>
      {[
        ["Signups", s.signups, "everyone who joined through your link"],
        ["Profiled", s.profiled, "completed a matching profile — these count"],
        ["Owed to you", dollars(s.owed_cents), "accrues when members pay; released by payout runs"],
        ["Paid to you", dollars(s.paid_cents), "all-time, via Stripe"],
      ].map(([t, v, sub]) => (
        <div key={t} style={card}>
          <div style={label}>{t}</div>
          <div style={{ fontFamily: MONO, fontSize: 34, marginTop: 6 }}>{v}</div>
          <div style={{ color: "#7c818b", fontSize: 12.5, marginTop: 6 }}>{sub}</div>
        </div>
      ))}
    </section>
  );

  return (
    <div style={{ background: "#0c0d10", color: "#f1f2f4", minHeight: "100vh",
      fontFamily: "'Hanken Grotesk',system-ui,sans-serif", WebkitFontSmoothing: "antialiased" }}>
      <style>{`.ad-btn:hover { filter: brightness(1.08); } @media (max-width:840px){ .ad-grid{ grid-template-columns:1fr !important; } }`}</style>

      <nav style={{ maxWidth: 1040, margin: "0 auto", padding: "26px 32px",
        display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <a href="/affiliates" onClick={(e) => { e.preventDefault(); navigate("/affiliates"); }}
           style={{ textDecoration: "none", color: "inherit" }}>
          <span style={{ fontFamily: SERIF, fontSize: 25 }}>glonet<span style={{ color: ACCENT }}>.ai</span></span>
          <span style={{ fontFamily: MONO, fontSize: 10, letterSpacing: "0.26em", color: "#6b6f78",
            textTransform: "uppercase", marginLeft: 12 }}>Affiliate&nbsp;Dashboard</span>
        </a>
        <a href="/affiliates" className="al-link" onClick={(e) => { e.preventDefault(); navigate("/affiliates"); }}
           style={{ color: "#b6b9c0", textDecoration: "none", fontSize: 14 }}>The program →</a>
      </nav>

      <main style={{ maxWidth: 1040, margin: "0 auto", padding: "6px 32px 90px" }}>
        {preview && (
          <div style={{ fontFamily: MONO, fontSize: 12, letterSpacing: "0.18em", textTransform: "uppercase",
            color: AMBER, border: `1px dashed ${AMBER}`, borderRadius: 10, padding: "10px 14px", marginBottom: 18 }}>
            Preview — sample numbers, not real data. Remove ?preview=1 for your own dashboard.
          </div>
        )}
        {/* Whose screen this is. Named, not merely flagged: an unlabeled view-as
            is indistinguishable from your own dashboard, which is how an admin
            ends up reporting someone else's numbers as theirs. */}
        {s?.viewing_as && (
          <div style={{ display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap",
            fontSize: 13.5, color: ACCENT, border: `1px solid ${ACCENT}`,
            background: "rgba(116,160,245,0.09)", borderRadius: 10,
            padding: "10px 14px", marginBottom: 18 }}>
            <span style={{ fontFamily: MONO, fontSize: 11, letterSpacing: "0.18em",
              textTransform: "uppercase" }}>Admin view</span>
            <span style={{ color: "#d7dae0" }}>
              You are looking at <strong>{s.viewing_as.name || s.viewing_as.email}</strong>
              {s.viewing_as.name ? ` (${s.viewing_as.email})` : ""} — their real numbers,
              exactly as they see them. Read-only: nothing here can be changed on their behalf.
            </span>
            {/* Plain /admin on purpose: the console restores the last tab from
                sessionStorage, so this lands back on Affiliates by itself — a
                ?tab= would be decoration the console does not read. */}
            <a href="/admin" style={{ color: ACCENT, marginLeft: "auto" }}>← back to admin</a>
          </div>
        )}
        {state === "loading" && <div style={{ color: "#7c818b" }}>Loading your numbers…</div>}

        {state === "forbidden" && (
          <div style={{ ...card, textAlign: "center", padding: "60px 30px" }}>
            <div style={{ fontFamily: SERIF, fontSize: 30, marginBottom: 12 }}>
              Viewing someone else&apos;s dashboard is for admins.
            </div>
            <div style={{ color: "#9a9ea7", fontSize: 15 }}>
              Drop the <span style={{ fontFamily: MONO }}>?as_user_id=</span> from the address
              to see your own.
            </div>
          </div>
        )}

        {state === "anon" && (
          <div style={{ ...card, textAlign: "center", padding: "60px 30px" }}>
            <div style={{ fontFamily: SERIF, fontSize: 30, marginBottom: 12 }}>Sign in to see your dashboard.</div>
            <button className="ad-btn" onClick={() => navigate("/signin", { state: { from: "/affiliates/dashboard" } })}
                    style={{ ...primaryBtn, fontSize: 15, padding: "13px 26px" }}>
              Sign in
            </button>
          </div>
        )}

        {state === "none" && s && (
          <div style={{ ...card, textAlign: "center", padding: "60px 30px" }}>
            <div style={{ fontFamily: SERIF, fontSize: 30, marginBottom: 10 }}>
              {s.application_status === "pending" ? "Your application is in review."
                : s.application_status === "rejected" ? "Your application wasn't accepted."
                : "You're not an affiliate yet."}
            </div>
            <div style={{ color: "#9a9ea7", marginBottom: 24, fontSize: 15 }}>
              {s.application_status === "pending"
                ? "We review daily — approval creates your link and emails your payout setup."
                : "The program takes people who own a real audience and will bring it."}
            </div>
            <div style={{ marginBottom: 18 }}>
              <a href="/affiliates/dashboard?preview=1" style={{ color: "#7c818b", fontSize: 13 }}
                 onClick={(e) => { e.preventDefault(); window.location.href = "/affiliates/dashboard?preview=1"; }}>
                See a preview of the dashboard →
              </a>
            </div>
            {s.application_status !== "pending" && (
              <button className="ad-btn" onClick={() => navigate("/affiliates?apply=1")}
                      style={{ ...primaryBtn, fontSize: 15, padding: "13px 26px" }}>
                {s.application_status === "rejected" ? "Make a stronger case" : "Apply for a slot"}
              </button>
            )}
          </div>
        )}

        {state === "ready" && s && (
          <>
            {/* tab strip */}
            <div data-testid="aff-dash-tabs" style={{ display: "flex", gap: 8, flexWrap: "wrap", margin: "6px 0 20px" }}>
              {TABS.map(([k, t]) => (
                <button key={k} className="ad-btn" data-testid={`aff-dash-tab-${k}`} onClick={() => setTab(k)}
                        style={{ fontFamily: MONO, fontSize: 12, letterSpacing: ".12em", textTransform: "uppercase",
                          color: tab === k ? "#0c0d10" : "#b6b9c0",
                          background: tab === k ? ACCENT : "rgba(255,255,255,0.05)",
                          border: "1px solid " + (tab === k ? ACCENT : "rgba(255,255,255,0.12)"),
                          cursor: "pointer", padding: "9px 18px", borderRadius: 999 }}>
                  {t}
                </button>
              ))}
            </div>

            {/* ————— TODAY ————— */}
            {tab === "today" && (
              <>
                <section style={{ ...card, display: "flex", flexWrap: "wrap", gap: 26, alignItems: "center",
                  borderColor: isPartner ? "rgba(116,160,245,0.45)" : "rgba(255,255,255,0.08)" }}>
                  <div style={{ flex: "1 1 260px" }}>
                    <div style={label}>Your tier</div>
                    <div style={{ fontFamily: SERIF, fontSize: 44, lineHeight: 1.1,
                      color: isPartner ? ACCENT : "#f1f2f4" }}>
                      {isPartner ? "Partner" : "Standard"}
                    </div>
                    <div style={{ color: "#9a9ea7", fontSize: 15, marginTop: 6 }}>
                      You earn <b style={{ color: "#f1f2f4" }}>{s.pct != null ? `${s.pct}%` : "the full host share"}</b> of
                      what each of your members actually pays, for{" "}
                      <b style={{ color: "#f1f2f4" }}>{s.window_months || "unlimited"} months per member</b>.
                      Terms lock on every member the moment you refer them.
                    </div>
                    {s.suspended && (
                      <div style={{ color: "#e57373", fontFamily: MONO, fontSize: 12, marginTop: 10 }}>
                        PAYOUTS SUSPENDED — contact the team.
                      </div>
                    )}
                  </div>
                  <div style={{ flex: "1 1 300px" }}>
                    <div style={label}>{isPartner ? `Keeping Partner (last 90 days)` : `Earning Partner (last 90 days)`}</div>
                    <div style={{ display: "flex", alignItems: "baseline", gap: 8, marginTop: 6 }}>
                      <span style={{ fontFamily: MONO, fontSize: 40, color: progress >= 1 ? GREEN : ACCENT }}>
                        {s.profiled_90d || 0}
                      </span>
                      <span style={{ color: "#7c818b", fontSize: 15 }}>
                        of {isPartner ? keepTarget : earnTarget} profiled signups, your quota
                      </span>
                    </div>
                    <div style={{ height: 8, borderRadius: 999, background: "rgba(255,255,255,0.08)", marginTop: 10 }}>
                      <div style={{ height: "100%", borderRadius: 999, width: `${progress * 100}%`,
                        background: progress >= 1 ? GREEN : ACCENT, transition: "width .5s" }} />
                    </div>
                    <div style={{ color: "#7c818b", fontSize: 13, marginTop: 8 }}>
                      {isPartner
                        ? (progress >= 1 ? "Quota met — Partner is safe this quarter."
                           : `Partner is kept by performing: your quota is ${keepTarget} profiled signups per rolling 90 days (2% of the audience you claimed, 100 to 600). A hosted Water Cooler meetup with 20+ live attendees credits 20.`)
                        : `Partner pays ${s.program?.partner_pct ?? 50}% for ${s.program?.partner_window_months ?? 12} months per member. Hit your quota of ${earnTarget} profiled signups in 90 days and you take a slot. ${Math.max(0, (s.program?.partner_slots ?? 100) - (s.partners_used || 0))} of ${s.program?.partner_slots ?? 100} open.`}
                    </div>
                  </div>
                </section>

                <section className="ad-grid" style={{ display: "grid", gridTemplateColumns: "1fr 1.4fr", gap: 18, marginTop: 18 }}>
                  <div style={{ ...card, borderColor: "rgba(116,160,245,0.35)" }}>
                    <div style={label}>Do this next</div>
                    <div style={{ fontFamily: SERIF, fontSize: 24, margin: "10px 0 6px" }}>{nextStep?.title}</div>
                    <div style={{ color: "#9a9ea7", fontSize: 14, lineHeight: 1.6, marginBottom: 16 }}>{nextStep?.why}</div>
                    <button className="ad-btn" onClick={nextStep?.act} disabled={!nextStep?.act}
                            style={{ ...primaryBtn, ...(nextStep?.act ? {} : { opacity: 0.45, cursor: "default" }) }}>
                      {nextStep?.cta}
                    </button>
                  </div>
                  <div style={card}>
                    <div style={label}>Your link</div>
                    <div style={{ fontFamily: MONO, fontSize: 15, margin: "10px 0 14px", wordBreak: "break-all", color: "#dfe1e5" }}>
                      {shareLink}
                    </div>
                    <button className="ad-btn" onClick={copy} style={primaryBtn}>
                      {copied ? "Copied ✓" : "Copy link"}
                    </button>
                    <div style={{ color: "#7c818b", fontSize: 12.5, marginTop: 12 }}>
                      Anyone who signs up after opening it is credited to you — including people who
                      join free now and subscribe later. Disclose the relationship when you share it.
                    </div>
                  </div>
                </section>

                <FourNumbers />

                {/* member discount picker: what their people pay in year one */}
                <section style={{ ...card, marginTop: 18 }}>
                  <div style={label}>Your member discount</div>
                  <div style={{ color: "#b6b9c0", fontSize: 14, margin: "10px 0 14px", maxWidth: "68ch" }}>
                    People who join through your link get this off their first year
                    once paying starts. Full price is estimated at $20 a month.
                  </div>
                  <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
                    {[[60, 8, "converts best"], [40, 12, "pays you more per member"]].map(([d, price, tag]) => {
                      const active = (s.referral_discount_pct ?? 60) === d;
                      return (
                        <button key={d} className="ad-btn" disabled={locked || savingDiscount}
                                onClick={() => setDiscount(d)}
                                style={{ flex: "1 1 220px", textAlign: "left",
                                  cursor: locked ? "default" : "pointer",
                                  background: active ? "rgba(116,160,245,0.14)" : "rgba(255,255,255,0.025)",
                                  border: active ? `1px solid ${ACCENT}` : "1px solid rgba(255,255,255,0.12)",
                                  borderRadius: 12, padding: "14px 16px", color: "#f1f2f4" }}>
                          <div style={{ fontFamily: MONO, fontSize: 20, color: active ? ACCENT : "#f1f2f4" }}>
                            {d}% off
                          </div>
                          <div style={{ fontSize: 13.5, color: "#9a9ea7", marginTop: 4 }}>
                            they pay about ${price} a month; {tag}
                          </div>
                          {active && (
                            <div style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: ".14em",
                              textTransform: "uppercase", color: ACCENT, marginTop: 8 }}>
                              your current pick
                            </div>
                          )}
                        </button>
                      );
                    })}
                  </div>
                  {discountNote && (
                    <div style={{ color: discountNote.startsWith("Could") ? AMBER : GREEN,
                      fontSize: 13, marginTop: 12 }}>{discountNote}</div>
                  )}
                  <div style={{ color: "#7c818b", fontSize: 12.5, marginTop: 12, maxWidth: "72ch" }}>
                    Going deeper also upgrades people you already sent who are not
                    paying yet. A member who already pays keeps their price. Going
                    shallower only changes future referrals. You are paid
                    {s.pct != null ? ` ${s.pct}%` : " your share"} of what they actually pay.
                  </div>
                </section>

                <section style={{ ...card, marginTop: 18 }}>
                  <div style={label}>Checklist</div>
                  <ul style={{ listStyle: "none", padding: 0, margin: "12px 0 0", fontSize: 14.5, lineHeight: 2 }}>
                    <li style={{ color: s.payout_ready ? GREEN : AMBER }}>
                      {s.payout_ready ? "✓" : "○"} Payout setup {s.payout_ready ? "complete" : "— finish it or money holds"}
                    </li>
                    <li style={{ color: (s.outreach_entries || 0) > 0 ? GREEN : AMBER }}>
                      {(s.outreach_entries || 0) > 0 ? "✓" : "○"} First outreach logged
                      {(s.outreach_entries || 0) > 0 ? ` (${s.outreach_entries} entr${s.outreach_entries === 1 ? "y" : "ies"})` : ""}
                    </li>
                    <li style={{ color: (s.profiled_90d || 0) >= (isPartner ? keepTarget : earnTarget) ? GREEN : AMBER }}>
                      {(s.profiled_90d || 0) >= (isPartner ? keepTarget : earnTarget) ? "✓" : "○"} Quota:{" "}
                      {s.profiled_90d || 0}/{isPartner ? keepTarget : earnTarget} profiled signups in the last 90 days
                    </li>
                    <li style={{ color: "#7c818b" }}>○ Host a Water Cooler meetup (counts toward your quota)</li>
                  </ul>
                </section>
              </>
            )}

            {/* ————— OUTREACH ————— */}
            {tab === "outreach" && (
              <>
                {/* 1 · the words. Was its own tab until 2026-08-26; copying one
                    now fills in the log below instead of sending you elsewhere. */}
                <section style={card} data-testid="aff-messages">
                  <div style={label}>1 · Pick a message</div>
                  <div style={{ color: "#b6b9c0", fontSize: 14, margin: "10px 0 4px", maxWidth: "68ch" }}>
                    Your link is already in each one. Copy, paste, send — then change
                    whatever you like; it sounds better in your own words.
                  </div>
                  <div style={{ color: "#7c818b", fontSize: 12.5, marginBottom: 16 }}>
                    Most signups come from the second message, not the first.
                  </div>
                  <div style={{ display: "grid", gap: 12 }}>
                    {(s.share_templates || []).map((t) => (
                      <details key={t.template_id}
                               style={{ background: "rgba(255,255,255,0.025)",
                                 border: "1px solid rgba(255,255,255,0.07)", borderRadius: 12,
                                 padding: "12px 14px" }}>
                        <summary style={{ cursor: "pointer", listStyle: "none", display: "flex",
                          alignItems: "center", justifyContent: "space-between", gap: 12 }}>
                          <span>
                            <span style={{ fontSize: 15, color: "#f1f2f4" }}>
                              {t.name.replace(/^Share · /, "")}
                            </span>
                            <span style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: ".14em",
                              textTransform: "uppercase", color: "#6b6f78", marginLeft: 10 }}>
                              {t.channel}
                            </span>
                          </span>
                          <button className="ad-btn"
                                  onClick={(e) => { e.preventDefault(); copyTemplate(t); }}
                                  style={{ fontSize: 13, fontWeight: 600,
                                    color: copiedId === t.template_id ? "#0c0d10" : "#f1f2f4",
                                    background: copiedId === t.template_id ? GREEN : "transparent",
                                    border: copiedId === t.template_id ? "none" : "1px solid rgba(255,255,255,0.22)",
                                    cursor: "pointer", padding: "8px 16px", borderRadius: 999,
                                    whiteSpace: "nowrap" }}>
                            {copiedId === t.template_id ? "Copied ✓" : "Copy"}
                          </button>
                        </summary>
                        {t.subject && (
                          <div style={{ fontFamily: MONO, fontSize: 12.5, color: "#9a9ea7", marginTop: 12 }}>
                            Subject: {t.subject}
                          </div>
                        )}
                        <pre style={{ whiteSpace: "pre-wrap", fontFamily: "inherit", fontSize: 14,
                          lineHeight: 1.6, color: "#c3c6cc", margin: "10px 0 0" }}>{t.body}</pre>
                        {t.notes && (
                          <div style={{ color: "#7c818b", fontSize: 12.5, marginTop: 10 }}>{t.notes}</div>
                        )}
                      </details>
                    ))}
                  </div>
                  <div style={{ color: "#7c818b", fontSize: 12.5, marginTop: 14 }}>
                    Say you are an affiliate when you share — it is the rule, and people
                    mind less than you think.
                  </div>
                </section>

                {/* 2 · the log, pre-filled by the copy above, and the post link
                    that calls the crew (shout-outs, 2026-08-26). */}
                <section style={{ ...card, marginTop: 18, borderColor: "rgba(240,180,41,0.35)" }}
                         data-testid="aff-log">
                  <div style={label}>2 · Log what you did</div>
                  <div style={{ color: "#b6b9c0", fontSize: 14, margin: "10px 0 16px", maxWidth: "68ch" }}>
                    You reach your people your own way. Log it here so your effort is
                    visible next to your results, to you and to us.
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                    <select value={logForm.channel} disabled={locked}
                            onChange={(e) => setLogForm((f) => ({ ...f, channel: e.target.value }))}
                            aria-label="channel" style={{ ...inputStyle, minWidth: 130 }}>
                      {CHANNELS.map(([v, t]) => <option key={v} value={v} style={{ color: "#111" }}>{t}</option>)}
                    </select>
                    <input type="number" min="0" max="100000" placeholder="how many people" disabled={locked}
                           value={logForm.contacted}
                           onChange={(e) => setLogForm((f) => ({ ...f, contacted: e.target.value }))}
                           aria-label="how many people" style={{ ...inputStyle, width: 150 }} />
                    <input type="date" value={logForm.day} disabled={locked}
                           onChange={(e) => setLogForm((f) => ({ ...f, day: e.target.value }))}
                           aria-label="day (defaults to today)" title="Defaults to today"
                           style={{ ...inputStyle, width: 160, colorScheme: "dark" }} />
                    <input placeholder="note (optional): what and to whom" disabled={locked}
                           value={logForm.note} maxLength={500}
                           onChange={(e) => setLogForm((f) => ({ ...f, note: e.target.value }))}
                           aria-label="note" style={{ ...inputStyle, flex: "1 1 220px" }} />
                  </div>
                  <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center",
                    marginTop: 10 }}>
                    <input placeholder="paste the link to your post (LinkedIn or X)" disabled={locked}
                           value={logForm.post_url} maxLength={2000}
                           data-testid="aff-post-url"
                           onChange={(e) => setLogForm((f) => ({ ...f, post_url: e.target.value }))}
                           aria-label="post link" style={{ ...inputStyle, flex: "1 1 320px" }} />
                    <button className="ad-btn" disabled={locked || logBusy} onClick={submitLog}
                            data-testid="aff-log-btn" style={primaryBtn}>
                      {logBusy ? "Saving…" : "I sent it — log it"}
                    </button>
                  </div>
                  <div style={{ color: AMBER, fontSize: 12.5, marginTop: 10, maxWidth: "72ch" }}>
                    Paste the link and we'll ask a few other affiliates to take a look and
                    give it a shout-out. The sooner you log it, the better it travels.
                  </div>
                  {logNote && (
                    <div style={{ color: logNote.startsWith("Logged —") ? GREEN : AMBER,
                      fontSize: 13, marginTop: 12, maxWidth: "72ch" }} data-testid="aff-log-note">
                      {logNote}
                    </div>
                  )}
                  <div style={{ color: "#7c818b", fontSize: 12.5, marginTop: 14, maxWidth: "72ch" }}>
                    Honesty rule: signups and money are counted from your link, never
                    from this log. The log shows effort; the link shows results.
                  </div>
                </section>

                <section style={{ ...card, marginTop: 18 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 12 }}>
                    <div style={label}>3 · Your outreach</div>
                    <div style={{ fontFamily: MONO, fontSize: 13, color: "#9a9ea7" }}>
                      {(outreach?.total_contacted || 0).toLocaleString()} people reached, self-reported
                    </div>
                  </div>
                  {!outreach && <div style={{ color: "#7c818b", marginTop: 12 }}>Loading…</div>}
                  {outreach && (outreach.outreach || []).length === 0 && (
                    <div style={{ color: "#7c818b", marginTop: 12 }}>
                      Nothing logged yet. The first entry takes ten seconds.
                    </div>
                  )}
                  {outreach && (outreach.outreach || []).length > 0 && (
                    <table style={{ width: "100%", marginTop: 14, fontSize: 13.5, borderCollapse: "collapse" }}>
                      <tbody>
                        {outreach.outreach.map((r) => (
                          <tr key={r.outreach_id} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                            <td style={{ padding: "8px 0", color: "#9a9ea7", width: "15%",
                              verticalAlign: "top" }}>{r.day}</td>
                            <td style={{ color: "#b6b9c0", width: "13%", textTransform: "capitalize",
                              verticalAlign: "top" }}>{r.channel}</td>
                            <td style={{ fontFamily: MONO, width: "10%", verticalAlign: "top" }}>{r.contacted}</td>
                            <td style={{ color: "#7c818b", verticalAlign: "top" }}>
                              {r.note || ""}
                              {/* The row that keeps filling in after the fact is
                                  what earns the next pasted link. */}
                              {r.post && (
                                <div style={{ marginTop: 5, fontSize: 12.5 }} data-testid="aff-post-line">
                                  <span style={{ color: GREEN }}>
                                    {r.post.asked} asked · {r.post.looks} took a look
                                    {" · "}{r.post.shoutouts} gave a shout-out
                                  </span>
                                  <span style={{ color: r.post.signups ? AMBER : "#6b6f78", marginLeft: 8 }}>
                                    {r.post.signups} signup{r.post.signups === 1 ? "" : "s"}
                                  </span>
                                  {r.post.status === "stopped" && (
                                    <span style={{ color: "#6b6f78", marginLeft: 8 }}>· finished</span>
                                  )}
                                </div>
                              )}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </section>
              </>
            )}


            {/* ————— RESULTS ————— */}
            {tab === "results" && (
              <>
                <FourNumbers />
                <section style={{ ...card, marginTop: 18 }}>
                  <div style={label}>Payouts</div>
                  <div className="ad-grid" style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 12, marginTop: 12 }}>
                    {[["Last 7 days", s.paid_7d], ["Last 30 days", s.paid_30d], ["Last 60 days", s.paid_60d],
                      ["Last 90 days", s.paid_90d], ["All-time", s.paid_cents]].map(([t, v]) => (
                      <div key={t} style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.07)",
                        borderRadius: 12, padding: "12px 14px" }}>
                        <div style={{ fontFamily: MONO, fontSize: 10.5, letterSpacing: "0.14em", textTransform: "uppercase", color: "#6b6f78" }}>{t}</div>
                        <div style={{ fontFamily: MONO, fontSize: 20, marginTop: 6 }}>{dollars(v)}</div>
                      </div>
                    ))}
                  </div>
                  {(s.recent_payouts || []).length > 0 && (
                    <table style={{ width: "100%", marginTop: 16, fontSize: 13.5, borderCollapse: "collapse" }}>
                      <tbody>
                        {s.recent_payouts.map((r, i) => (
                          <tr key={i} style={{ borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                            <td style={{ padding: "8px 0", color: "#9a9ea7", width: "22%" }}>{r.at ? new Date(r.at).toLocaleDateString() : ""}</td>
                            <td style={{ color: "#7c818b" }}>{r.stream === "one_time" ? "room / ticket" : "subscription"}</td>
                            <td style={{ fontFamily: MONO }}>{dollars(r.amount_cents)}</td>
                            <td style={{ textAlign: "right", fontFamily: MONO, fontSize: 11, letterSpacing: "0.1em", textTransform: "uppercase",
                              color: r.status === "sent" ? GREEN : r.status === "held" ? AMBER : "#9aa1ad" }}>{r.status}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                  <div style={{ color: "#7c818b", fontSize: 12.5, marginTop: 10 }}>
                    Payouts are Stripe transfers, sent when your members&apos; payments settle.
                    Money is counted from your link. Nobody pays yet during early access;
                    earning starts when matching turns on.
                  </div>
                </section>
              </>
            )}

            {/* ————— PRIVATE ROOMS (owner rulings 2026-08-24) ————— */}
            {tab === "rooms" && (
              <>
                {!roomsData && <div style={{ color: "#7c818b" }}>Loading your rooms…</div>}
                {roomsData && !roomsData.enabled && (
                  <section style={{ ...card, textAlign: "center", padding: "50px 30px" }}>
                    <div style={{ fontFamily: SERIF, fontSize: 28 }}>Private room tickets aren&apos;t open yet.</div>
                    <div style={{ color: "#9a9ea7", marginTop: 10, fontSize: 15, maxWidth: "56ch",
                      marginLeft: "auto", marginRight: "auto" }}>
                      When they open you&apos;ll sell paid entry to your own room in the Water
                      Cooler — you keep 92.9% of every ticket after card fees.
                    </div>
                  </section>
                )}
                {roomsData && roomsData.enabled && (
                  <>
                    <section style={card}>
                      <div style={label}>Sell a room</div>
                      <div style={{ color: "#b6b9c0", fontSize: 14, margin: "10px 0 16px", maxWidth: "68ch" }}>
                        Name it, price it, create it. You get three codes: a <b>buy link</b> to
                        promote, a <b>free code</b> for the guests who draw the crowd, and a{" "}
                        <b>room token</b> you paste when you open the room at the Water Cooler.
                        Card fees come off the top; you keep 92.9% of the rest — released after
                        your room actually runs.
                      </div>
                      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
                        <input placeholder="Room title (e.g. VC Office Hours)" disabled={locked}
                               value={rtForm.title} maxLength={80}
                               onChange={(e) => setRtForm((f) => ({ ...f, title: e.target.value }))}
                               aria-label="room title" style={{ ...inputStyle, flex: "2 1 240px" }} />
                        <input type="number" min="3" step="1" placeholder="price $ (min 3)" disabled={locked}
                               value={rtForm.price}
                               onChange={(e) => setRtForm((f) => ({ ...f, price: e.target.value }))}
                               aria-label="ticket price in dollars" style={{ ...inputStyle, width: 150 }} />
                        <input placeholder="one line on what happens inside (optional)" disabled={locked}
                               value={rtForm.description} maxLength={1000}
                               onChange={(e) => setRtForm((f) => ({ ...f, description: e.target.value }))}
                               aria-label="description" style={{ ...inputStyle, flex: "3 1 280px" }} />
                        <button className="ad-btn" data-testid="create-ticket" disabled={locked || rtBusy}
                                onClick={createTicket} style={primaryBtn}>
                          {rtBusy ? "Creating…" : "Create ticket"}
                        </button>
                      </div>
                      {rtNote && <div style={{ color: "#9a9ea7", fontSize: 13.5, marginTop: 10 }}>{rtNote}</div>}
                    </section>

                    {(roomsData.tickets || []).map((rt) => {
                      const money = (c) => `$${((c || 0) / 100).toLocaleString(undefined, { minimumFractionDigits: 2 })}`;
                      const CodeRow = ({ name, value, hint }) => (
                        <div style={{ display: "flex", alignItems: "baseline", gap: 10, flexWrap: "wrap",
                          padding: "7px 0", borderTop: "1px solid rgba(255,255,255,0.06)" }}>
                          <span style={{ ...label, fontSize: 10, width: 92 }}>{name}</span>
                          <span style={{ fontFamily: MONO, fontSize: 15, userSelect: "all" }}>{value}</span>
                          <button className="ad-btn" disabled={locked}
                                  onClick={() => rtCopy(rt.ticket_id, name, value)}
                                  style={{ fontSize: 12, color: ACCENT, background: "none",
                                    border: "none", cursor: "pointer", padding: 0 }}>
                            {rtCopied === `${rt.ticket_id}:${name}` ? "copied ✓" : "copy"}
                          </button>
                          <span style={{ color: "#7c818b", fontSize: 12.5 }}>{hint}</span>
                        </div>
                      );
                      return (
                        <section key={rt.ticket_id} style={{ ...card, marginTop: 18 }} data-testid={`ticket-${rt.room_code}`}>
                          <div style={{ display: "flex", alignItems: "baseline", gap: 14, flexWrap: "wrap" }}>
                            <div style={{ fontFamily: SERIF, fontSize: 24 }}>{rt.title}</div>
                            <div style={{ fontFamily: MONO, fontSize: 18 }}>{money(rt.price_cents)}</div>
                            {!rt.active && (
                              <span style={{ ...label, color: AMBER }}>retired</span>
                            )}
                          </div>
                          <div style={{ margin: "14px 0 4px" }}>
                            <CodeRow name="buy link" value={rt.buy_url}
                                     hint="promote this — people pay here and get their ticket code" />
                            <CodeRow name="free code" value={rt.free_code}
                                     hint={`for your headline guests, on their honor · used ${rt.free_redemptions || 0}×`} />
                            <CodeRow name="room token" value={rt.room_token}
                                     hint="paste into the door tool when you open the room — reusable every session" />
                          </div>
                          <div style={{ display: "flex", gap: 22, flexWrap: "wrap", marginTop: 14,
                            fontSize: 13.5, color: "#b6b9c0" }}>
                            <span><b style={{ color: "#f1f2f4" }}>{rt.sold || 0}</b> sold</span>
                            <span><b style={{ color: "#f1f2f4" }}>{rt.delivered || 0}</b> delivered</span>
                            <span><b style={{ color: "#f1f2f4" }}>{money(rt.revenue_cents)}</b> revenue</span>
                            <span style={{ color: AMBER }}>{money(rt.held_cents)} held until your room runs</span>
                            {/* delivered but not yet transferred — without this line the
                                held money looks like it vanished the day the room ran */}
                            {(rt.releasing_cents || 0) > 0 && (
                              <span style={{ color: "#9fc4f8" }}>{money(rt.releasing_cents)} released, on its way</span>
                            )}
                            <span style={{ color: GREEN }}>{money(rt.paid_cents)} paid to you</span>
                          </div>
                          <div style={{ color: "#7c818b", fontSize: 12.5, marginTop: 12 }}>
                            Ticket holders who never get their room are refunded automatically
                            after 14 days — showing up is what releases your money.
                          </div>
                        </section>
                      );
                    })}
                    {!roomsData.payout_ready && (roomsData.tickets || []).length > 0 && (
                      <section style={{ ...card, marginTop: 18, borderColor: `${AMBER}55` }}>
                        <div style={{ ...label, color: AMBER }}>Payouts not set up</div>
                        <div style={{ color: "#b6b9c0", fontSize: 14.5, marginTop: 8 }}>
                          Ticket money accrues either way, but it can&apos;t reach you until
                          Stripe payout setup is done (see the Today tab).
                        </div>
                      </section>
                    )}
                  </>
                )}
              </>
            )}
          </>
        )}
      </main>
    </div>
  );
}
